import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AttachmentListLabels, AttachmentViewerLabels } from "@outpave/ds";

/**
 * The strings `AttachmentList` renders itself. Several interpolate the file
 * name, so those keys are functions rather than strings; the package ships no
 * i18n, and it has no empty-state label, so "no files" stays at the call site.
 */
export function useAttachmentListLabels(): AttachmentListLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      download: (name: string) => t("Download {{name}}", { name }),
      print: (name: string) => t("Print {{name}}", { name }),
      remove: (name: string) => t("Remove {{name}}", { name }),
      open: (name: string) => t("Open {{name}}", { name }),
      addFiles: t("Add files"),
      limitReached: t("Limit reached"),
      limitReachedHint: (max: number) => t("Limit reached ({{max}})", { max }),
    }),
    [t],
  );
}

/** The strings the fullscreen viewer renders itself. */
export function useAttachmentViewerLabels(): AttachmentViewerLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      close: t("Close"),
      previous: t("Previous"),
      next: t("Next"),
      zoomIn: t("Zoom In"),
      zoomOut: t("Zoom Out"),
      fit: t("Reset Zoom"),
      download: (name: string) => t("Download {{name}}", { name }),
      print: (name: string) => t("Print {{name}}", { name }),
      remove: (name: string) => t("Remove {{name}}", { name }),
      stripSelect: (name: string) => t("Open {{name}}", { name }),
      counter: (position: number, total: number) =>
        t("Receipt {{current}} of {{total}}", { current: position, total }),
    }),
    [t],
  );
}
